import React, { useEffect } from 'react';

interface IconButtonProps {
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  icon: string;
  isLoading?: boolean;
  loadingIcon?: string;
  variant?: 'default' | 'primary';
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * A reusable icon button component with hover effects and loading state
 */
const IconButton: React.FC<IconButtonProps> = ({
  onClick,
  disabled = false,
  title = '',
  icon,
  isLoading = false,
  loadingIcon = 'refresh',
  variant = 'default',
  size = 'medium',
  className = '',
}) => {
  // Add Material Icons font if not already loaded
  useEffect(() => {
    if (!document.querySelector('link[href*="material-icons"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.head.appendChild(link);
    }
  }, []);

  // Determine sizes based on the size prop
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          button: { minWidth: '32px', minHeight: '32px', padding: '6px' },
          icon: { fontSize: '16px' }
        };
      case 'large':
        return {
          button: { minWidth: '48px', minHeight: '48px', padding: '12px' },
          icon: { fontSize: '24px' }
        };
      case 'medium':
      default:
        return {
          button: { minWidth: '40px', minHeight: '40px', padding: '8px' },
          icon: { fontSize: '20px' }
        };
    }
  };

  // Determine colors based on the variant prop
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          background: '#3949ab',
          border: 'none',
          color: '#ffffff'
        };
      case 'default':
      default:
        return {
          background: '#fff',
          border: '1px solid #e0e0e0',
          color: '#3949ab'
        };
    }
  };

  const sizeStyles = getSizeStyles();
  const variantStyles = getVariantStyles();

  return (
    <>
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={`icon-button ${className}`}
        style={{
          ...variantStyles,
          ...sizeStyles.button,
          borderRadius: '8px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.7 : 1,
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transform: 'translateY(0)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <span
          className="material-icons"
          style={{
            ...sizeStyles.icon,
            color: variant === 'primary' ? '#ffffff' : '#3949ab',
            animation: isLoading ? 'spin 1s linear infinite' : 'none',
          }}
        >
          {isLoading ? loadingIcon : icon}
        </span>
      </button>

      {/* Add CSS for spin animation if not already present */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default IconButton;
